import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { steeringBoard, secretariatTeam } from "@/mocks/homeData";

function getInitials(name: string) {
  const parts = name.split(" ");
  return parts
    .filter(
      (p) =>
        p.length > 1 &&
        !p.startsWith("Engr.") &&
        !p.startsWith("Md.") &&
        !p.startsWith("Prof.") &&
        !p.startsWith("Dr."),
    )
    .map((n) => n[0])
    .slice(0, 2)
    .join("");
}

function Avatar({
  name,
  image,
  size = "md",
}: {
  name: string;
  image?: string;
  size?: "sm" | "md" | "lg";
}) {
  const sizeClasses = {
    sm: "w-12 h-12 text-sm",
    md: "w-14 h-14 md:w-16 md:h-16 text-base md:text-lg",
    lg: "w-16 h-16 md:w-20 md:h-20 text-lg md:text-xl",
  };

  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className={`${sizeClasses[size]} rounded-full object-cover object-top shrink-0 border-2 border-tie-teal/40`}
      />
    );
  }

  return (
    <div
      className={`${sizeClasses[size]} rounded-full bg-tie-teal/20 border-2 border-tie-teal/40 flex items-center justify-center shrink-0`}
    >
      <span className="font-serif font-bold text-tie-teal-light">
        {getInitials(name)}
      </span>
    </div>
  );
}

export default function GovernanceSection() {
  const leaders = steeringBoard.filter((m) => m.isLeadership);
  const members = steeringBoard.filter((m) => !m.isLeadership);

  return (
    <section className="relative bg-tie-navy py-16 md:py-24 overflow-hidden">
      {/* TIE Logo Watermark */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-[0.05]">
        <img
          src="https://storage.readdy-site.link/project_files/3ad77496-7e33-4bee-aed3-853667869f76/3a6b7789-d222-4345-bb6d-4232140708c8_TIE1.png?v=90453bddcbe5657cab713355118e545f"
          alt=""
          className="w-48 md:w-72 h-auto object-contain"
        />
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/20 mb-4 md:mb-6">
              <span className="text-xs font-semibold text-tie-teal-light uppercase tracking-wider">
                / Steering Board
              </span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl font-bold text-white leading-tight">
              Leadership That Drives Innovation Forward
            </h2>
            <p className="mt-3 md:mt-4 text-base md:text-lg text-white/60">
              The TIE Steering Board provides strategic direction, governance
              oversight, and institutional accountability for Bangladesh&apos;s
              textile innovation ecosystem.
            </p>
          </div>
        </ScrollReveal>

        {/* Leadership Row */}
        <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto mb-8 md:mb-10">
          {leaders.map((member, index) => (
            <ScrollReveal key={member.name} delay={index * 150}>
              <div className="relative flex items-start gap-4 p-5 md:p-6 rounded-2xl bg-white/[0.07] border border-tie-teal/30 hover:border-tie-teal/50 transition-all duration-300 h-full overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-tie-gold rounded-l-2xl" />
                <Avatar name={member.name} image={member.image} size="lg" />
                <div className="min-w-0">
                  <span className="inline-flex items-center gap-1.5 text-[10px] md:text-xs font-bold text-tie-gold uppercase tracking-wider mb-1">
                    <i className="ri-vip-crown-line text-[10px]" />
                    {member.title}
                  </span>
                  <h3 className="text-base md:text-lg font-bold text-white leading-snug">
                    {member.name}
                  </h3>
                  <p className="text-sm text-white/50 mt-1 leading-relaxed">
                    {member.company}
                  </p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Divider */}
        <ScrollReveal delay={200}>
          <div className="flex items-center justify-center gap-3 mb-8 md:mb-10 max-w-6xl mx-auto">
            <div className="h-px flex-1 bg-white/10" />
            <p className="text-center text-xs font-semibold text-white/40 uppercase tracking-wider whitespace-nowrap">
              Board Members
            </p>
            <div className="h-px flex-1 bg-white/10" />
          </div>
        </ScrollReveal>

        {/* Board Members */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto mb-10 md:mb-14">
          {members.map((member, index) => (
            <ScrollReveal key={member.name} delay={index * 100}>
              <div className="flex items-start gap-4 p-5 md:p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-tie-teal/30 hover:bg-white/[0.07] transition-all duration-300 h-full">
                <Avatar name={member.name} image={member.image} size="md" />
                <div className="min-w-0">
                  <h3 className="text-base md:text-lg font-bold text-white leading-snug">
                    {member.name}
                  </h3>
                  <p className="text-sm text-tie-teal-light mt-1 font-medium">
                    {member.title}
                  </p>
                  <p className="text-sm text-white/50 mt-0.5">
                    {member.company}
                  </p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Secretariat */}
        <ScrollReveal delay={200}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-5 md:mb-6">
              <div className="h-px w-12 bg-white/20" />
              <p className="text-center text-xs font-semibold text-white/50 uppercase tracking-wider">
                Secretariat
              </p>
              <div className="h-px w-12 bg-white/20" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
              {secretariatTeam.map((member) => (
                <div
                  key={member.name}
                  className="flex items-start gap-4 p-5 md:p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-tie-teal/30 hover:bg-white/[0.07] transition-all duration-300 h-full"
                >
                  <Avatar name={member.name} image={member.image} size="md" />
                  <div className="min-w-0 text-left">
                    <h3 className="text-base md:text-lg font-bold text-white leading-snug">
                      {member.name}
                    </h3>
                    <p className="text-sm text-tie-teal-light mt-1 font-medium">
                      {member.role}
                    </p>
                    {member.contact && (
                      <p className="text-sm text-white/50 mt-0.5">
                        {member.contact}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}